import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import { EventHeader, EventTabs } from "../../../../components/Events/Event";
import ClientSatisfactionSurvey from "../SatisfactionSurvey";
import messages from "../../../../assets/locale/messages";
import * as clientPortalEventsApi from "../../../../network/apis/clientPortalEvents";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  getPortalApiErrorMessage,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import { showErrorMsg, formatEventStatusTabLabel, appendActiveUrlTabBreadcrumb } from "../../../../utils/Helpers";
import {
  EVENT_VIEW_DEFAULT_TAB,
  getAllowedEventViewTabs,
  getEventViewTabFromSearch,
  getEventViewTabLabels,
} from "../../../../utils/eventViewTabs";
import { shouldShowEventEvaluationsTab } from "../../../../utils/companyPortal/eventEditRules";

import "./View.scss";

const CLIENT_EVALUATIONS_TAB = "clientEvaluations";

const ViewEvent = () => {
  const { id: routeId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const decodedId = routeId ? decodeURIComponent(String(routeId)) : "";

  // Kept in a ref so switching language does not re-run the event fetch.
  const generalErrMsgRef = useRef(shared.generalErrMsg);
  generalErrMsgRef.current = shared.generalErrMsg;

  const [event, setEvent] = useState(null);

  const evaluationsTab = useMemo(
    () =>
      shouldShowEventEvaluationsTab(event)
        ? [{ value: CLIENT_EVALUATIONS_TAB, label: events.viewEvents.evaluations }]
        : [],
    [event, events.viewEvents.evaluations],
  );

  const fetchEvent = useCallback(
    async ({ silent = false } = {}) => {
      if (!decodedId) {
        setEvent(null);
        return;
      }

      try {
        const res = await clientPortalEventsApi.getClientPortalEvent(decodedId);
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        setEvent({ id: unwrapped?.id ?? decodedId, ...attrs });
      } catch (e) {
        if (!silent) {
          showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
          setEvent(null);
          const status = e?.response?.status;
          if (status === 404 || status === 403) {
            history.replace(ROUTE_PATHS.clientPortalEvents);
          }
        }
      }
    },
    [decodedId, history],
  );

  useEffect(() => {
    if (!decodedId) {
      setEvent(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await clientPortalEventsApi.getClientPortalEvent(decodedId);
        if (cancelled) return;
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        setEvent({ id: unwrapped?.id ?? decodedId, ...attrs });
      } catch (e) {
        if (!cancelled) {
          showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
          setEvent(null);
          const status = e?.response?.status;
          if (status === 404 || status === 403) {
            history.replace(ROUTE_PATHS.clientPortalEvents);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [decodedId, history]);

  const allowedTabValues = useMemo(
    // Client portal: no applicants/evaluations/timesheet/reports/payroll/heatmap
    // flags here — only the shared Client Reports tab is added.
    () =>
      getAllowedEventViewTabs(
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        true,
      ),
    [],
  );

  const activeTab = useMemo(
    () =>
      getEventViewTabFromSearch(location.search, allowedTabValues) ??
      EVENT_VIEW_DEFAULT_TAB,
    [allowedTabValues, location.search],
  );

  const eventViewTabLabels = useMemo(
    () => getEventViewTabLabels(events.viewEvents),
    [events.viewEvents],
  );

  const breadcrumbList = useMemo(
    () =>
      appendActiveUrlTabBreadcrumb({
        breadcrumbs: [
          {
            label: events.listEvents.listEventsBreadcrumb,
            path: ROUTE_PATHS.clientPortalEvents,
          },
          {
            label: formatEventStatusTabLabel(event?.status, events.listEvents.eventTabs),
            path: event?.status
              ? `${ROUTE_PATHS.clientPortalEvents}?tab=${event.status}`
              : ROUTE_PATHS.clientPortalEvents,
          },
          { label: event?.name || "", path: "" },
        ],
        activeTab,
        defaultTab: EVENT_VIEW_DEFAULT_TAB,
        tabLabelsByValue: eventViewTabLabels,
        viewPathname: location.pathname,
        search: location.search,
      }),
    [
      activeTab,
      event?.name,
      event?.status,
      eventViewTabLabels,
      events.listEvents.eventTabs,
      events.listEvents.listEventsBreadcrumb,
      location.pathname,
      location.search,
    ],
  );

  return (
    <Box
      className="client-event-view"
      sx={{
        marginTop: "var(--space-32)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-16)",
        paddingBottom: "var(--space-32)",
      }}
    >
      <Breadcrumbs
        list={breadcrumbList}
        className="client-event-view__breadcrumbs"
        BreadCrumbHasBackIcon={false}
      />
      <EventHeader
        event={event}
        editLabel={events.viewEvents.editBtn}
        showEditButton={false}
      />
      <EventTabs
        event={event}
        onRefreshEvent={fetchEvent}
        portalVariant="client-portal"
        additionalTabs={evaluationsTab}
        renderAdditionalTab={(tab) =>
          tab === CLIENT_EVALUATIONS_TAB ? (
            <ClientSatisfactionSurvey event={event} />
          ) : null
        }
      />
    </Box>
  );
};

export default ViewEvent;
